import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import NumberField from 'components/number-field';
import { editManualCharge } from 'graphql/ledger/mutations';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAlert from 'utils/alert';

import Title from './title';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DatesAndConfigurationForm, EstateAddress, ParsedAmountEntry } from '../services';

const useStyles = makeStyles((theme: any) => ({
  amountsRoot: {
    marginTop: theme.spacing(-1),
    marginLeft: theme.spacing(-1),
  },
  amountCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.line}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    marginBottom: theme.spacing(1),
  },
  amountLabel: {
    fontWeight: 500,
    flex: 1,
    marginRight: theme.spacing(1.5),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  amountInputWrapper: {
    minWidth: 140,
    flexShrink: 0,
  },
  amountInput: {
    fontFamily: 'monospace',
    textAlign: 'right',
  },
  cashIndicator: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  cashIcon: {
    marginRight: theme.spacing(1),
  },
  cashMessage: {
    fontWeight: 600,
  },
  sectionLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  loanSectionLabel: {
    fontWeight: 600,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
}));

type FormAmountsProps = {
  estatesMap: Map<string, EstateAddress>;
  loansMap: Map<string, string>;
  purchasePriceEntries: ParsedAmountEntry[];
  loanAmountEntries: ParsedAmountEntry[];
};

const AmountCard: React.FC<{
  amount: number;
  entry: ParsedAmountEntry;
  referenceInfo?: string;
}> = ({ amount, entry, referenceInfo }) => {
  const classes = useStyles();
  const latestValues = React.useRef<{ floatValue?: number }>({});
  const { id } = useParams<{ id: string }>();
  const alert = useAlert();
  const [inputValue, setInputValue] = React.useState(amount);
  const { mutate: editEntryMutation, ...responseMutation } = useMutation(editManualCharge, {
    onSuccess: () => {
      responseMutation.reset();
      alert('Order updated.', 'success');
    },
    onError: () => {
      responseMutation.reset();
      alert('Unable to update order.', 'error');
    },
  });

  const handleBlur = () => {
    const { floatValue } = latestValues.current;
    if (floatValue !== undefined) {
      editEntryMutation({
        id,
        accountingId: entry._id,
        data: { amount_override: floatValue },
      });
    }
  };

  return (
    <Grid item xs={12}>
      <Box className={classes.amountCard}>
        {referenceInfo && (
          <Typography
            variant="body2"
            className={classes.amountLabel}
            title={referenceInfo}
          >
            {referenceInfo}
          </Typography>
        )}
        <Box className={classes.amountInputWrapper}>
          <NumberField
            value={inputValue}
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
            isNumericString
            prefix="$"
            fullWidth={false}
            InputProps={{
              inputProps: {
                className: classes.amountInput,
              },
            }}
            onValueChange={(values) => {
              latestValues.current = values;
              setInputValue(values?.floatValue || 0);
            }}
            naked
            handleBlur={handleBlur}
          />
        </Box>
      </Box>
    </Grid>
  );
};

const FormAmounts: React.FC<FormAmountsProps> = ({
  estatesMap,
  loansMap,
  purchasePriceEntries,
  loanAmountEntries,
}) => {
  const { control } = useFormContext<DatesAndConfigurationForm>();
  const { t } = useTranslation();
  const classes = useStyles();
  const watchedCash = useWatch({ control, name: "cash_only" });

  return (
    <Grid
      container
      direction="row"
      spacing={2}
      className={classes.amountsRoot}
    >
      <Grid item xs={12}>
        <Tooltip title={t('orders:cashOnlyTooltip')}>
          {watchedCash ?
            <Box className={classes.cashIndicator}>
              <FontAwesomeIcon
                icon="money-bill"
                size="1x"
                className={classes.cashIcon}
              />
              <Typography variant="body1" className={classes.cashMessage}>{t('orders:cashOnlyOrderMessage')}</Typography>
            </Box> :
            <Box className={classes.cashIndicator}>
              <FontAwesomeIcon
                icon="landmark"
                size="1x"
                className={classes.cashIcon}
              />
              <Typography variant="body1" className={classes.cashMessage}>{t('orders:notCashOnlyOrderMessage')}</Typography>
            </Box>}
        </Tooltip>
      </Grid>

      {/* Purchase Price Section */}
      {(purchasePriceEntries.length > 0 || loanAmountEntries.length > 0) && (
        <Grid item xs={12}>
          <Title>{t('datesAndConfig:amounts')}</Title>
        </Grid>
      )}

      {purchasePriceEntries.length > 0 && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle2" className={classes.sectionLabel}>
              {t('datesAndConfig:purchase-prices')}
            </Typography>
          </Grid>
          {purchasePriceEntries.map((entry) => {
            const estate = estatesMap.get(entry.referenceId);
            return (
              <AmountCard
                key={entry._id}
                amount={entry.amount}
                entry={entry}
                referenceInfo={estate?.address?.street_address || `Reference: ${entry.referenceId}`}
              />
            );
          })}
        </>
      )}

      {/* Loan Amounts Section */}
      {loanAmountEntries.length > 0 && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle2" className={classes.loanSectionLabel}>
              {t('datesAndConfig:loan-amounts')}
            </Typography>
          </Grid>
          {loanAmountEntries.map((entry) => {
            const loanNumber = loansMap.get(entry.referenceId);
            return (
              <AmountCard
                key={entry._id}
                amount={entry.amount}
                entry={entry}
                referenceInfo={loanNumber || `Reference: ${entry.referenceId}`}
              />
            );
          })}
        </>
      )}

    </Grid>
  );
};

export default FormAmounts;
